import { FC, ReactNode, useRef, useState } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';

import { useClickOutside } from 'hooks';

import * as S from './Tooltip.styles';

type Props = {
  text: string | null;
  timeout?: number;
  children: ReactNode;
  onOpen?: () => void;
  onClose?: () => void;
};

let idCounter = 0;

export const Tooltip: FC<Props> = ({ text, timeout, children, onOpen, onClose }) => {
  const [isOpen, setIsOpen] = useState(false);
  const idRef = useRef(`tooltip-${++idCounter}`);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  useClickOutside(wrapperRef, () => setIsOpen(false), isOpen && timeout === undefined);

  const handleClick = () => {
    if (isOpen) {
      if (timerRef.current) clearTimeout(timerRef.current);
      setIsOpen(false);
      if (onClose) onClose();
      return;
    }
    setIsOpen(true);
    if (onOpen) onOpen();
    if (timeout !== undefined) {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        setIsOpen(false);
        if (onClose) onClose();
      }, timeout);
    }
  };

  return (
    <>
      <S.Anchor ref={wrapperRef} data-tooltip-id={idRef.current} onClick={handleClick}>
        {children}
      </S.Anchor>
      <ReactTooltip
        id={idRef.current}
        isOpen={isOpen}
        content={text || undefined}
        place="bottom"
        offset={10}
        border="1px solid rgba(155, 89, 182, 0.35)"
        style={{
          backgroundColor: 'rgba(10, 8, 30, 0.95)',
          color: 'rgba(255, 255, 255, 0.85)',
          fontFamily: "'Share Tech Mono', monospace",
          fontSize: '12px',
          letterSpacing: '0.04em',
          backdropFilter: 'blur(12px)',
          boxShadow: '0 8px 24px rgba(0, 0, 0, 0.5), 0 0 1px rgba(155, 89, 182, 0.2)',
          borderRadius: '8px',
          padding: '7px 12px',
          maxWidth: '220px',
          zIndex: 1000,
        }}
        opacity={1}
      />
    </>
  );
};
